import React from 'react';
import styled from 'styled-components';
import { useWorkspaceError } from '@/contexts/WorkspaceErrorContext';
import { formatAzureError } from '@/utils/errors/parseAzureError';

const ErrorContainer = styled.div`
  padding: 16px;
  background-color: #fff3f3;
  border: 1px solid #f56565;
  border-radius: 4px;
  margin: 16px 0;
  position: relative;
`;

const ErrorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const ErrorTitle = styled.h3`
  color: #c53030;
  margin: 0;
  font-size: 1.1rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #c53030;
  cursor: pointer;
  font-size: 1.5rem;
  line-height: 1;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(197, 48, 48, 0.1);
  }
`;

const ErrorList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ErrorItem = styled.div`
  padding: 12px;
  padding-right: 36px;
  background-color: white;
  border: 1px solid #feb2b2;
  border-radius: 4px;
  position: relative;
`;

const ErrorMessage = styled.p`
  margin: 0 0 8px 0;
  color: #742a2a;
  font-weight: 500;
`;

const AzureErrorBadge = styled.div`
  display: inline-block;
  padding: 4px 8px;
  background-color: #fed7d7;
  border: 1px solid #fc8181;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #742a2a;
  margin-left: 8px;
`;

const AzureErrorDetails = styled.div`
  margin-top: 8px;
  padding: 8px;
  background-color: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 4px;
  font-size: 0.875rem;
`;

const AzureErrorRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const AzureErrorLabel = styled.span`
  font-weight: 600;
  color: #742a2a;
  min-width: 80px;
`;

const AzureErrorValue = styled.span`
  color: #2d3748;
`;

const ErrorDetails = styled.details`
  margin-top: 8px;
  font-size: 0.875rem;
`;

const ErrorSummary = styled.summary`
  cursor: pointer;
  color: #c53030;
  font-weight: 500;
  margin-bottom: 8px;
  user-select: none;

  &:hover {
    text-decoration: underline;
  }
`;

const ErrorStack = styled.pre`
  margin: 8px 0 0 0;
  padding: 8px;
  background-color: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 12px;
  font-family: monospace;
  color: #2d3748;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-x: auto;
`;

const ErrorTimestamp = styled.div`
  font-size: 0.75rem;
  color: #999;
  margin-top: 4px;
`;

interface WorkspaceErrorDisplayProps {
  workspaceId?: string;
  maxErrors?: number;
}

/**
 * WorkspaceErrorDisplay Component
 *
 * Displays errors from the WorkspaceErrorContext visually.
 * Only shows errors for the specified workspace (if provided).
 *
 * @param workspaceId - Optional workspace ID to filter errors
 * @param maxErrors - Maximum number of errors to display (default: 10)
 */
export const WorkspaceErrorDisplay: React.FC<WorkspaceErrorDisplayProps> = ({
  workspaceId,
  maxErrors = 10,
}) => {
  const { errors, clearError, hasErrors } = useWorkspaceError();

  // Filter errors by workspace if workspaceId is provided
  const filteredErrors = workspaceId
    ? errors.filter((err) => err.workspaceId === workspaceId)
    : errors;

  // Limit the number of errors displayed
  const displayErrors = filteredErrors.slice(-maxErrors);

  if (!hasErrors || displayErrors.length === 0) {
    return null;
  }

  const formatTimestamp = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(date);
  };

  return (
    <ErrorContainer>
      <ErrorHeader>
        <ErrorTitle>
          {displayErrors.length} Error{displayErrors.length !== 1 ? 's' : ''}{' '}
          Detected
        </ErrorTitle>
        <CloseButton
          onClick={() => {
            // Clear all displayed errors for this workspace
            // Work backwards through indices to avoid shifting issues
            const indicesToRemove: number[] = [];
            filteredErrors.forEach((filteredError) => {
              const index = errors.findIndex((e) => e === filteredError);
              if (index !== -1 && !indicesToRemove.includes(index)) {
                indicesToRemove.push(index);
              }
            });
            // Sort descending and remove (to avoid index shifting)
            indicesToRemove
              .sort((a, b) => b - a)
              .forEach((index) => {
                clearError(index);
              });
          }}
          title="Clear all errors"
        >
          ×
        </CloseButton>
      </ErrorHeader>
      <ErrorList>
        {displayErrors.map((errorData, displayIndex) => {
          // Find the index in the full errors array
          const displayMessage = errorData.azureError
            ? formatAzureError(errorData.azureError)
            : errorData.error.message;

          return (
            <ErrorItem key={`${errorData.timestamp.getTime()}-${displayIndex}`}>
              <ErrorMessage>
                {displayMessage}
                {errorData.azureError && (
                  <AzureErrorBadge>Azure Error</AzureErrorBadge>
                )}
              </ErrorMessage>
              {errorData.azureError && (
                <AzureErrorDetails>
                  {errorData.azureError.status && (
                    <AzureErrorRow>
                      <AzureErrorLabel>Status:</AzureErrorLabel>
                      <AzureErrorValue>
                        {errorData.azureError.status}
                        {errorData.azureError.statusText &&
                          ` - ${errorData.azureError.statusText}`}
                      </AzureErrorValue>
                    </AzureErrorRow>
                  )}
                  {errorData.azureError.errorCode && (
                    <AzureErrorRow>
                      <AzureErrorLabel>Error Code:</AzureErrorLabel>
                      <AzureErrorValue>
                        {errorData.azureError.errorCode}
                      </AzureErrorValue>
                    </AzureErrorRow>
                  )}
                  {errorData.azureError.exceptionType && (
                    <AzureErrorRow>
                      <AzureErrorLabel>Exception:</AzureErrorLabel>
                      <AzureErrorValue>
                        {errorData.azureError.exceptionType}
                      </AzureErrorValue>
                    </AzureErrorRow>
                  )}
                  {errorData.azureError.message && (
                    <AzureErrorRow>
                      <AzureErrorLabel>Message:</AzureErrorLabel>
                      <AzureErrorValue>
                        {errorData.azureError.message}
                      </AzureErrorValue>
                    </AzureErrorRow>
                  )}
                </AzureErrorDetails>
              )}
              <ErrorTimestamp>
                {formatTimestamp(errorData.timestamp)}
              </ErrorTimestamp>
              {errorData.errorInfo && (
                <ErrorDetails>
                  <ErrorSummary>View error details</ErrorSummary>
                  <div>
                    <ErrorStack>
                      {errorData.errorInfo.componentStack}
                    </ErrorStack>
                  </div>
                </ErrorDetails>
              )}
              {errorData.error.stack && (
                <ErrorDetails>
                  <ErrorSummary>View stack trace</ErrorSummary>
                  <ErrorStack>{errorData.error.stack}</ErrorStack>
                </ErrorDetails>
              )}
              <CloseButton
                onClick={() => {
                  // Find the current index (may have changed after previous deletions)
                  const currentIndex = errors.findIndex((e) => e === errorData);
                  if (currentIndex !== -1) {
                    clearError(currentIndex);
                  }
                }}
                title="Remove this error"
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  fontSize: '1.2rem',
                }}
              >
                ×
              </CloseButton>
            </ErrorItem>
          );
        })}
      </ErrorList>
      {filteredErrors.length > maxErrors && (
        <div style={{ marginTop: '12px', fontSize: '0.875rem', color: '#666' }}>
          Showing {maxErrors} of {filteredErrors.length} errors. Clear errors to
          see more.
        </div>
      )}
    </ErrorContainer>
  );
};

export default WorkspaceErrorDisplay;
